import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'AADC';

// Map of routes to page titles
const PAGE_TITLES = {
  '/': 'Home',
  '/login': 'Sign In',
  '/register': 'Join',
  '/clubs': 'Club Directory',
  '/forums': 'Forums',
  '/marketplace': 'Marketplace',
  '/vendors': 'Service Providers',
  '/services': 'Pet Services',
  '/parks': 'Dog Parks',
  '/events': 'Events',
  '/photos': 'Photo Gallery',
  '/vote': 'Vote',
  '/about': 'About',
  '/about/story': 'Our Story',
  '/about/privacy': 'Privacy Policy',
  '/about/terms': 'Terms of Service',
  '/about/service-agreement': 'Service Agreement',
  '/faq': 'FAQ',
  '/beta-feedback': 'Beta Feedback',
  '/membership': 'Membership',
  '/settings': 'Settings',
  '/settings/profile': 'Profile Settings',
  '/settings/pets': 'My Pets',
  '/settings/notifications': 'Notification Settings',
  '/admin': 'Admin Dashboard',
  '/admin/users': 'User Management',
  '/admin/reports': 'Reports',
  '/admin/settings': 'Admin Settings',
  '/vbb/operations': 'World Pets Operations',
  '/request-club': 'Request a Club',
  '/test-captcha': 'CAPTCHA Test',
  '/breeds': 'Breed Guide',
  '/jobs': 'Job Board',
  '/sports': 'Sports & Gatherings',
  '/channels': 'Social Channels',
  '/blog': 'Blog',
  '/club-blog': 'Club News',
  '/deals': 'Deals',
  '/roadmap': 'Roadmap',
  '/communities': 'Communities',
  '/about/future': 'Our Vision',
  '/contact': 'Contact Us',
  '/about/bylaws': 'Club Bylaws',
};

/**
 * Custom hook to set dynamic page titles
 * @param {string} customTitle - Optional custom title to override route-based title
 */
export const usePageTitle = (customTitle = null) => {
  const location = useLocation();

  useEffect(() => {
    let title = customTitle;

    if (!title) {
      // Try exact match first
      title = PAGE_TITLES[location.pathname];

      // If no exact match, try matching patterns
      if (!title) {
        if (location.pathname.startsWith('/club/')) {
          title = 'Club';
        } else if (location.pathname.startsWith('/forums/')) {
          title = 'Forum Post';
        } else if (location.pathname.startsWith('/profile/')) {
          title = 'Profile';
        } else if (location.pathname.startsWith('/marketplace/')) {
          title = 'Marketplace';
        } else if (location.pathname.startsWith('/events/')) {
          title = 'Event';
        } else if (location.pathname.startsWith('/admin/')) {
          title = 'Admin';
        } else if (location.pathname.startsWith('/settings/')) {
          title = 'Settings';
        }
      }
    }

    // Set the document title
    if (title) {
      document.title = `${title} | ${SITE_NAME}`;
    } else {
      document.title = SITE_NAME;
    }

    // Cleanup - reset to default when unmounting (optional)
    return () => {
      // Don't reset - let the next page set its own title
    };
  }, [location.pathname, customTitle]);
};

/**
 * Set page title imperatively (for use outside of React components)
 * @param {string} title - Page title
 */
export const setPageTitle = (title) => {
  if (title) {
    document.title = `${title} | ${SITE_NAME}`;
  } else {
    document.title = SITE_NAME;
  }
};

export default usePageTitle;
